/* eslint-disable no-restricted-syntax --
 * This module is palette DATA, not app styling: the array below lists the
 * selectable brand palettes (each an intrinsic hex pair + name) and the color
 * math that derives light/dark token values from them. The "use a token / use
 * t('brand.name')" rule targets hardcoded colors in component styling, which
 * does not apply here — the whole point is to compute and inject token values.
 */
/**
 * Color-theme (palette) switcher logic.
 *
 * Ported from prototype/palette.js. Brand stays single-source: applyPalette
 * overrides --color-brand-* and --viz-* on :root, recomputed per light/dark
 * (dark uses a luminous lift so the brand reads on dark surfaces). The shell
 * token bridge (styles/shell-tokens.css) derives --primary/--ring/etc. from
 * these, and Tailwind `bg-brand` content tracks them too.
 */

export interface Palette {
  id: string;
  name: string;
  /** base brand primary (light) */
  primary: string;
  /** brand secondary / accent */
  accent: string;
  /** Picker swatch fill (defaults to `primary`); the dark surface themes use it to read as black. */
  swatch?: string;
  /** Dark-only theme: selecting it switches the app into dark mode. */
  forceDark?: boolean;
  /** Explicit dark surface ramp (base canvas / panels / raised) overriding the hue-tinted default. */
  dark?: { surface: string; muted: string; sunk: string };
}

/** Wellow indigo/orange first → the default. */
export const PALETTES: Palette[] = [
  { id: 'wellow', name: 'Wellow', primary: '#2D4FE8', accent: '#F08236' },
  { id: 'iq', name: 'IQ ERP', primary: '#0B3B40', accent: '#C8985A' },
  { id: 'angularjs', name: 'AngularJS', primary: '#B52E31', accent: '#1A1A1A' },
  { id: 'answers', name: 'Answers', primary: '#136AD5', accent: '#FB8A2E' },
  { id: 'arriva', name: 'Arriva', primary: '#004D73', accent: '#007FDB' },
  { id: 'atos', name: 'Atos', primary: '#0066A1', accent: '#FF6319' },
  { id: 'delta', name: 'Delta', primary: '#C8102E', accent: '#003A70' },
  { id: 'dunked', name: 'Dunked', primary: '#2DA9D7', accent: '#212A3E' },
  { id: 'gitter', name: 'Gitter', primary: '#46BC99', accent: '#ED1965' },
  { id: 'london', name: 'London U.', primary: '#000099', accent: '#CC3333' },
  { id: 'olark', name: 'Olark', primary: '#744DA8', accent: '#1FB3E0' },
  { id: 'portfolium', name: 'Portfolium', primary: '#0099FF', accent: '#FB0A2A' },
  { id: 'sonos', name: 'Sonos', primary: '#1C5F4A', accent: '#D8A158' },
  { id: 'western-digital', name: 'Western Digital', primary: '#005195', accent: '#FFD400' },
  { id: 'indigo', name: 'IQ Indigo', primary: '#4338CA', accent: '#E0613E' },
  // Dark-only surface themes (force dark): a true-black OLED canvas and a
  // softer near-black, both keeping a clean blue/amber accent.
  {
    id: 'black', name: 'Black', primary: '#4C8DFF', accent: '#F0A23C',
    swatch: '#000000', forceDark: true,
    dark: { surface: '#000000', muted: '#0B0B0E', sunk: '#17181C' },
  },
  {
    id: 'near-black', name: 'Near black', primary: '#4C8DFF', accent: '#F0A23C',
    swatch: '#16181D', forceDark: true,
    dark: { surface: '#0C0D11', muted: '#15171D', sunk: '#1F222A' },
  },
];

export const DEFAULT_PALETTE = 'wellow';
const PALETTE_KEY = 'erp-palette';

/* ---- color math ---- */
function hex2rgb(h: string): [number, number, number] {
  let s = h.replace('#', '');
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
}
function rgb2hex(r: number, g: number, b: number): string {
  const c = (v: number) => ('0' + Math.round(Math.max(0, Math.min(255, v))).toString(16)).slice(-2);
  return '#' + c(r) + c(g) + c(b);
}
function rgb2hsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (mx + mn) / 2;
  if (mx !== mn) {
    const d = mx - mn;
    s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
    switch (mx) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return [h * 360, s * 100, l * 100];
}
function hsl2hex(h: number, s: number, l: number): string {
  h = ((h % 360) + 360) % 360; s = Math.max(0, Math.min(100, s)) / 100; l = Math.max(0, Math.min(100, l)) / 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number): number => {
    const k = (n + h / 30) % 12;
    return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return rgb2hex(f(0) * 255, f(8) * 255, f(4) * 255);
}
function hslOf(hex: string): { h: number; s: number; l: number } {
  const [h, s, l] = rgb2hsl(...hex2rgb(hex));
  return { h, s, l };
}
function adj(hex: string, dl: number): string {
  const c = hslOf(hex);
  return hsl2hex(c.h, c.s, Math.max(0, Math.min(100, c.l + dl)));
}
function isDull(hex: string): boolean {
  const c = hslOf(hex);
  return c.s < 14 || c.l < 10 || c.l > 92;
}
function deriveAccent(primary: string, accent: string): string {
  if (accent && !isDull(accent)) return accent;
  const c = hslOf(primary);
  return hsl2hex(c.h + 28, Math.min(85, c.s + 6), Math.min(60, Math.max(46, c.l + 8)));
}

/** Apply a palette's brand + viz tokens to :root, recomputed for the theme. */
export function applyPalette(id: string, isDark: boolean): void {
  if (typeof document === 'undefined') return;
  const p = PALETTES.find((x) => x.id === id) ?? PALETTES[0]!;
  const accent = deriveAccent(p.primary, p.accent);
  const pc = hslOf(p.primary), ac = hslOf(accent);
  const set = (k: string, v: string) => document.documentElement.style.setProperty(k, v);

  if (isDark) {
    const P = hsl2hex(pc.h, Math.min(78, pc.s), 66);
    const A = hsl2hex(ac.h, Math.min(82, ac.s), 66);
    set('--color-brand-primary', P);
    set('--color-brand-primary-hover', adj(P, -10));
    set('--color-brand-primary-active', adj(P, -20));
    set('--color-brand-secondary', A);
    set('--color-brand-secondary-hover', adj(A, +8));
    set('--color-on-primary', hsl2hex(pc.h, 40, 8));
    set('--viz-1', P);
    set('--viz-2', adj(P, +14));
    set('--viz-3', hsl2hex((pc.h + ac.h) / 2 + 8, 55, 60));
    set('--viz-4', A);
    set('--viz-5', adj(A, +12));
    set('--viz-6', hsl2hex(pc.h, 16, 64));
    // Dark background tracks the palette: a low-saturation tint of the brand
    // hue, so switching theme recolours the whole canvas (+ the glow base,
    // which reads --color-surface). Tuned so the default Wellow blue lands at
    // the ~#1A1D3A navy. Borders/text stay theme-fixed (white / translucent).
    // The black / near-black themes carry an explicit achromatic ramp instead.
    if (p.dark) {
      set('--color-surface', p.dark.surface);
      set('--color-surface-muted', p.dark.muted);
      set('--color-surface-sunk', p.dark.sunk);
    } else {
      set('--color-surface', hsl2hex(pc.h, 34, 16));
      set('--color-surface-muted', hsl2hex(pc.h, 33, 22));
      set('--color-surface-sunk', hsl2hex(pc.h, 31, 29));
    }
  } else {
    set('--color-brand-primary', p.primary);
    set('--color-brand-primary-hover', adj(p.primary, -8));
    set('--color-brand-primary-active', adj(p.primary, -18));
    set('--color-brand-secondary', accent);
    set('--color-brand-secondary-hover', adj(accent, -10));
    set('--color-on-primary', hsl2hex(pc.h, 30, 98));
    set('--viz-1', p.primary);
    set('--viz-2', adj(p.primary, +18));
    set('--viz-3', hsl2hex((pc.h + ac.h) / 2 + 8, 52, 48));
    set('--viz-4', accent);
    set('--viz-5', adj(accent, +10));
    set('--viz-6', hsl2hex(pc.h, 14, 56));
    // Light mode keeps the neutral surfaces from tokens.css — drop any dark
    // tint set by a prior dark render so the canvas returns to white.
    for (const k of ['--color-surface', '--color-surface-muted', '--color-surface-sunk']) {
      document.documentElement.style.removeProperty(k);
    }
  }
}

/** Small swatch accent used by the picker UI (independent of theme). */
export function swatchAccent(p: Palette): string {
  return deriveAccent(p.primary, p.accent);
}

export function loadPalette(): string {
  try {
    return window.localStorage.getItem(PALETTE_KEY) || DEFAULT_PALETTE;
  } catch {
    return DEFAULT_PALETTE;
  }
}

export function savePalette(id: string): void {
  try {
    window.localStorage.setItem(PALETTE_KEY, id);
  } catch {
    /* ignore */
  }
}
